/**
 * HTTP request queue management system for GeoServer
 * Controls the number of concurrent requests per session and prioritizes real-time
 * Includes persistence to survive singleton recreations
 */

import { connectionTimeout } from '../../configs/initialValues';
import { debugLogger, LogLevel } from '../debugLogger';

export type RequestPriority = 'HIGH' | 'MEDIUM' | 'LOW';

const REQUEST_CANCELLED = 'RequestCancelledError';

/**
 * Rejection of a queued request that was dropped before being sent (cancelLowPriorityRequests,
 * clearAll). Settling those promises lets their callers release the tile/source they hold;
 * left pending, they were retained forever.
 */
export const createRequestCancelledError = (): Error => {
  const error = new Error('Request cancelled');
  error.name = REQUEST_CANCELLED;
  return error;
};

export const isRequestCancelled = (error: unknown): boolean => (
  error instanceof Error && error.name === REQUEST_CANCELLED
);

export interface QueuedRequest {
  id: string;
  url: string;
  options: any; // RequestInit equivalent
  priority: RequestPriority;
  time: string;
  type: 'real-time' | 'preload' | 'standard' | 'unknown';
  resolve: (response: Response) => void;
  reject: (error: Error) => void;
}

// Global state for persistence across singleton recreations
const GLOBAL_STATE_KEY = 'REQUEST_QUEUE_GLOBAL_STATE';

interface PersistedState {
  requestCounter: number;
  activeRequestsCount: number; // Can't persist actual requests, just count
  lastInstanceId: string;
  timestamp: number;
}

// Helper function to save state to global object
function saveGlobalState(state: PersistedState) {
  (window as any)[GLOBAL_STATE_KEY] = state;
}

// Helper function to load state from global object
function loadGlobalState(): PersistedState | null {
  const state = (window as any)[GLOBAL_STATE_KEY];

  // Only restore state if it's recent (within connectionTimeout)
  if (state && Date.now() - state.timestamp < connectionTimeout) {
    return state;
  }

  return null;
}

interface ActiveRequest {
  controller: AbortController;
  priority: RequestPriority;
  timestamp: number;
}

interface RequestQueueConfig {
  maxConcurrentRequests: number;
  retryAttempts: number;
  retryDelay: number;
  requestTimeout: number;
}

class RequestQueueManager {
  private queue: QueuedRequest[] = [];

  private activeRequests: Map<string, ActiveRequest> = new Map();

  private config: RequestQueueConfig;

  private requestCounter = 0;

  private readonly instanceId: string;

  constructor(config?: Partial<RequestQueueConfig>) {
    // Generate unique ID to track singleton instances
    this.instanceId = Math.random().toString(36).substr(2, 9);

    // Try to restore state from previous instance
    const persistedState = loadGlobalState();
    if (persistedState) {
      this.requestCounter = persistedState.requestCounter;
      debugLogger.system(LogLevel.INFO, 'RequestQueue state restored from previous instance', {
        newInstanceId: this.instanceId,
        previousInstanceId: persistedState.lastInstanceId,
        restoredRequestCounter: this.requestCounter,
        previousActiveRequests: persistedState.activeRequestsCount,
        timeSinceLastInstance: Date.now() - persistedState.timestamp,
      });
    } else {
      debugLogger.system(LogLevel.INFO, 'RequestQueue instance created fresh', {
        instanceId: this.instanceId,
        timestamp: Date.now(),
        userAgent: navigator.userAgent.substring(0, 100),
      });
    }

    // eslint-disable-next-line no-underscore-dangle
    const env = (window as any)._env_ || {};
    this.config = {
      maxConcurrentRequests: parseInt(
        env.REACT_APP_MAX_CONCURRENT_REQUESTS || '3',
        10,
      ),
      retryAttempts: 2,
      retryDelay: 1000,
      requestTimeout: connectionTimeout,
      ...config,
    };

    // Save initial state
    this.saveCurrentState();
  }

  private saveCurrentState() {
    saveGlobalState({
      requestCounter: this.requestCounter,
      activeRequestsCount: this.activeRequests.size,
      lastInstanceId: this.instanceId,
      timestamp: Date.now(),
    });
  }

  /**
   * Adds a request to the queue with specific priority
   */
  async enqueueRequest(
    url: string,
    options?: any,
    priority: RequestPriority = 'MEDIUM',
  ): Promise<Response> {
    debugLogger.system(LogLevel.DEBUG, 'EnqueueRequest method called', {
      instanceId: this.instanceId,
      requestCounter: this.requestCounter,
      queueState: {
        queueLength: this.queue.length,
        activeRequests: this.activeRequests.size,
      },
      url: url.substring(0, 100) + (url.length > 100 ? '...' : ''),
    });

    return new Promise((resolve, reject) => {
      this.requestCounter += 1;
      const requestId = `req_${this.requestCounter}_${Date.now()}`;

      // Extract request type and time information from URL and headers
      const urlParams = new URLSearchParams(url.split('?')[1] || '');
      const timeParam = urlParams.get('time') || urlParams.get('TIME') || 'unknown';

      // Safely extract request type from headers
      let requestType = 'unknown';
      if (options?.headers) {
        const { headers } = options;
        if (headers instanceof Headers) {
          requestType = headers.get('X-Request-Type') || 'unknown';
        } else if (Array.isArray(headers)) {
          const typeHeader = headers.find(([key]) => key === 'X-Request-Type');
          requestType = typeHeader ? typeHeader[1] : 'unknown';
        } else if (typeof headers === 'object') {
          requestType = (headers as any)['X-Request-Type'] || 'unknown';
        }
      }

      const request: QueuedRequest = {
        id: requestId,
        url,
        options: options || {},
        priority,
        time: timeParam,
        type: requestType as any,
        resolve,
        reject,
      };

      debugLogger.queue(LogLevel.INFO, 'Enqueuing request', {
        id: requestId,
        type: requestType,
        priority,
        time: timeParam,
        url: url.substring(0, 100) + (url.length > 100 ? '...' : ''),
        queueLength: this.queue.length,
        activeRequests: this.activeRequests.size,
      });

      // Add to queue based on priority
      if (priority === 'HIGH') {
        this.queue.unshift(request);
      } else {
        this.queue.push(request);
      }

      debugLogger.queue(LogLevel.INFO, 'After enqueue - Queue state', {
        total: this.queue.length,
        active: this.activeRequests.size,
        priorities: {
          high: this.queue.filter((r) => r.priority === 'HIGH').length,
          medium: this.queue.filter((r) => r.priority === 'MEDIUM').length,
          low: this.queue.filter((r) => r.priority === 'LOW').length,
        },
        nextInQueue: this.queue.length > 0 ? {
          id: this.queue[0].id,
          priority: this.queue[0].priority,
        } : null,
      });

      // Save state after adding request
      this.saveCurrentState();

      this.processQueue();
    });
  }

  /**
   * Processes the request queue
   */
  private async processQueue(): Promise<void> {
    debugLogger.system(LogLevel.DEBUG, 'ProcessQueue called', {
      instanceId: this.instanceId,
      queueLength: this.queue.length,
      activeRequests: this.activeRequests.size,
      maxConcurrent: this.config.maxConcurrentRequests,
      canProcess: this.activeRequests.size < this.config.maxConcurrentRequests
        && this.queue.length > 0,
    });

    if (this.activeRequests.size >= this.config.maxConcurrentRequests
      || this.queue.length === 0) {
      return;
    }

    const request = this.queue.shift();
    if (!request) return;

    const controller = new AbortController();
    const requestId = request.id;

    this.activeRequests.set(requestId, {
      controller,
      priority: request.priority,
      timestamp: Date.now(),
    });

    // Save state after starting request
    this.saveCurrentState();

    // Declared outside the try so every exit path (success or failure) can clear it.
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          controller.abort();
          reject(new Error('Request timeout'));
        }, this.config.requestTimeout);
      });

      const response = await Promise.race([
        fetch(request.url, {
          ...request.options,
          signal: controller.signal,
        }),
        timeoutPromise,
      ]);

      this.activeRequests.delete(requestId);
      this.saveCurrentState();

      request.resolve(response);

      debugLogger.imageService(LogLevel.INFO, 'Image request completed successfully', {
        time: request.time,
        isRealTime: request.type === 'real-time',
        isPreload: request.type === 'preload',
        priority: request.priority,
        status: response.status,
      });

      // Continue processing queue
      this.processQueue();
    } catch (error) {
      this.activeRequests.delete(requestId);
      this.saveCurrentState();

      debugLogger.imageService(LogLevel.ERROR, 'Image request failed', {
        time: request.time,
        error: error instanceof Error ? error.message : 'Unknown error',
        priority: request.priority,
      });

      request.reject(error as Error);

      // Continue processing queue even after error
      this.processQueue();
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  }

  /**
   * Cancels low priority requests in the queue
   */
  cancelLowPriorityRequests(): void {
    const cancelled = this.queue.filter((request) => request.priority === 'LOW');
    this.queue = this.queue.filter((request) => request.priority !== 'LOW');

    if (cancelled.length) {
      cancelled.forEach((request) => request.reject(createRequestCancelledError()));
      debugLogger.queue(LogLevel.INFO, 'Cancelled low priority requests', {
        cancelledCount: cancelled.length,
        remainingInQueue: this.queue.length,
      });
      this.saveCurrentState();
    }
  }

  /**
   * Clears all pending requests from the queue
   */
  clearAll() {
    const cleared = this.queue;
    const clearedCount = cleared.length;
    this.queue = [];
    cleared.forEach((request) => request.reject(createRequestCancelledError()));

    debugLogger.queue(LogLevel.INFO, 'Cleared all pending requests', {
      clearedCount,
      instanceId: this.instanceId,
    });

    this.saveCurrentState();
    return clearedCount;
  }

  /**
   * Gets queue statistics
   */
  getStats() {
    return {
      queueLength: this.queue.length,
      activeRequests: this.activeRequests.size,
      maxConcurrent: this.config.maxConcurrentRequests,
      priorities: {
        high: this.queue.filter((r) => r.priority === 'HIGH').length,
        medium: this.queue.filter((r) => r.priority === 'MEDIUM').length,
        low: this.queue.filter((r) => r.priority === 'LOW').length,
      },
      instanceId: this.instanceId,
      requestCounter: this.requestCounter,
    };
  }
}

// Global singleton
let requestQueueInstance: RequestQueueManager | null = null;

// Function to get or create the singleton instance
function getRequestQueue(): RequestQueueManager {
  if (!requestQueueInstance) {
    debugLogger.system(LogLevel.INFO, 'Creating global RequestQueue singleton', {
      timestamp: Date.now(),
      location: 'module initialization',
    });
    requestQueueInstance = new RequestQueueManager();
  }
  return requestQueueInstance;
}

/**
 * Main function to make HTTP requests through the queue
 */
export async function queuedFetch(
  url: string,
  options?: any,
  priority: RequestPriority = 'MEDIUM',
): Promise<Response> {
  const queue = getRequestQueue();
  return queue.enqueueRequest(url, options, priority);
}

/**
 * Cancels low priority requests in the queue
 */
export function cancelLowPriorityRequests(): void {
  const queue = getRequestQueue();
  queue.cancelLowPriorityRequests();
}

/**
 * Gets queue statistics
 */
export function getQueueStats() {
  const queue = getRequestQueue();
  return queue.getStats();
}

// Exportar tipos
export { RequestQueueManager };

// Export the singleton instance for compatibility with existing code
export const requestQueue = getRequestQueue();

// Additional export for useRequestQueue hook
export function useRequestQueue() {
  return getRequestQueue();
}
